// AccountHead.jsx
import React, { useState, useEffect } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import { Button, Modal, Row, Col, Form, OverlayTrigger } from "react-bootstrap";
import { MdEdit, MdAddCircle, MdFilterList, MdDelete } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Tooltip } from '@mui/material';
import loading from "../../../assets/images/common/loading.gif";
import AppDataGrid from '../../../components/common/AppDataGrid';

const AccountHead = () => {
    const [accountHeads, setAccountHeads] = useState([]);
    const [allAccountHeads, setAllAccountHeads] = useState([]); // ✅ master copy for client-side filter
    const [groups, setGroups] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const navigate = useNavigate();
    const handleCloseFilterModal = () => setShowFilterModal(false);
    const [showFilterModal, setShowFilterModal] = useState(false);
    const handleShowFilterModal = () => setShowFilterModal(true);
    const baseUrl = process.env.REACT_APP_API_BASE_URL;
    const [isLoading, setIsLoading] = useState(true);
    const userData = sessionStorage.getItem('user');
    const userObj = userData ? JSON.parse(userData) : {};
    const readOnlyRoles = ["Class Teacher", "Teacher", "Class Incharge", "School Admin"];
    const canSubmit = !readOnlyRoles.includes(userObj.role_name?.trim());

    const [filter, setFilter] = useState({
        account_head_name: "",
        group_id: "",
        account_head_code: "", // ✅ already in state, just needs UI field + filter logic
    });

    const handleEditClick = (account_head_id) => {
        const accountHeadToEdit = accountHeads.find(ah => ah.account_head_id === account_head_id);
        if (accountHeadToEdit) {
            navigate("/finance/addaccounthead", { state: { accountHeadData: accountHeadToEdit } });
        } else {
            console.error(`Account Head with ID ${account_head_id} not found`);
        }
    };

    useEffect(() => {
        setIsLoading(true);
        Promise.all([fetchAccountHeads(), fetchGroups()]).finally(() => setIsLoading(false));
    }, []);

    const fetchAccountHeads = async () => {
        try {
            const response = await axios.get(baseUrl + "/account-head/", {
                params: { school_id: userObj.school_id }
            });
            const data = Array.isArray(response.data) ? response.data : [];
            setAccountHeads(data);
            setAllAccountHeads(data); // ✅ store master copy
        } catch (error) {
            console.error("Error fetching account heads:", error);
        }
    };

    const fetchGroups = async () => {
        try {
            const response = await axios.post(baseUrl + "/groupsfilter/", {
                action: "READ", // ✅ ensure correct action sent
                school_id: userObj.school_id,
            });
            setGroups(response.data || []);
        } catch (error) {
            console.error("Error fetching groups:", error);
        }
    };

    const columns = [
        {
            field: 'group_name',
            headerName: 'Group Name',
            flex: 1,
            headerAlign: 'center',
            align: "center",
            renderCell: (params) => <Tooltip title={params.value || '-'}><span>{params.value || '-'}</span></Tooltip>,
            sortable: true,
        },
        {
            field: 'group_code',
            headerName: 'Group Code',
            flex: 1,
            headerAlign: 'center',
            align: "center",
            renderCell: (params) => <Tooltip title={params.value || '-'}><span>{params.value || '-'}</span></Tooltip>,
            sortable: true,
        },
        {
            field: 'account_head_name',
            headerName: 'Account Head Name',
            flex: 1,
            headerAlign: 'center',
            align: "center",
            renderCell: (params) => <Tooltip title={params.value}><span>{params.value}</span></Tooltip>,
            sortable: true,
        },
        {
            field: 'account_head_code',
            headerName: 'Account Head Code',
            flex: 1,
            headerAlign: 'center',
            align: "center",
            renderCell: (params) => <Tooltip title={params.value || '-'}><span>{params.value || '-'}</span></Tooltip>,
            sortable: true,
        },
        {
            field: 'is_active',
            headerName: 'Status',
            flex: 1,
            headerAlign: 'center',
            align: "center",
            // ✅ FIX TC3: Render proper label — backend returns 'A'/'I' or 'Active'/'Inactive'
            renderCell: (params) => {
                const raw = params.value;
                const label =
                    raw === 'A' ? 'Active' :
                    raw === 'I' ? 'Inactive' :
                    (raw || '-'); // already a string like 'Active'/'Inactive'
                return <Tooltip title={label}><span>{label}</span></Tooltip>;
            },
            sortable: true,
        },
        ...(canSubmit ? [{
            field: 'actions',
            headerName: "Actions",
            flex: 1,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params) => (
                <div className="tableActions">
                    <span className="commonActionIcons" onClick={() => handleEditClick(params.row.account_head_id)}>
                        <MdEdit />
                    </span>
                    <span className="commonActionIcons" onClick={() => handleDeleteClick(params.row.account_head_id)}>
                        <MdDelete />
                    </span>
                </div>
            ),
        }] : [])
    ];

    const handleDeleteClick = async (account_head_id) => {
        const confirmDelete = window.confirm('Are you sure you want to change the status?');
        if (!confirmDelete) return;

        try {
            const response = await axios.delete(
                `${baseUrl}/account-head/${account_head_id}`,
                { params: { user: userObj.user_name } }
            );
            if (response.status < 200 || response.status >= 300) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            toast.success("Record Set to InActive");
            // ✅ FIX TC4: Re-fetch all records — inactive record stays visible in grid
            await fetchAccountHeads();
        } catch (error) {
            console.error('Error:', error);
            toast.error("Error deleting record");
        }
    };

    const searchableColumns = [
        (row) => row.account_head_name,
        (row) => row.account_head_code,
        (row) => row.group_name,
        (row) => row.group_code,  // ✅ include group_code in search too
        (row) => row.is_active,
    ];

    const filteredRecords = (accountHeads || []).filter((item) =>
        searchableColumns.some((selector) => {
            const value = selector(item);
            const stringValue = String(value || '').toUpperCase().replace(/[-\s]+/g, '');
            const normalizedQuery = searchQuery.toUpperCase().replace(/[-\s]+/g, '');
            return stringValue.includes(normalizedQuery);
        })
    );

    // ✅ FIX TC1: Client-side filter so group_code and all fields work reliably
    const handleFilterSubmit = (e) => {
        e.preventDefault();

        const ahName  = filter.account_head_name.trim().toLowerCase();
        const ahCode  = filter.account_head_code.trim().toLowerCase();
        const gId     = filter.group_id;

        if (!ahName && !ahCode && !gId) {
            setAccountHeads(allAccountHeads);
            setShowFilterModal(false);
            return;
        }

        const filtered = allAccountHeads.filter((row) => {
            const matchName  = !ahName || (row.account_head_name || "").toLowerCase().includes(ahName);
            const matchCode  = !ahCode || (row.account_head_code || "").toLowerCase().includes(ahCode); // ✅ TC1 fix
            const matchGroup = !gId   || String(row.group_id) === String(gId);
            return matchName && matchCode && matchGroup;
        });

        if (filtered.length === 0) {
            toast.info("No matching records found");
        }

        setAccountHeads(filtered);
        setShowFilterModal(false);
    };

    const handleReset = (e) => {
        e.preventDefault();
        setFilter({ account_head_name: "", group_id: "", account_head_code: "" });
        setAccountHeads(allAccountHeads); // ✅ restore full list without re-fetch
        setShowFilterModal(true);
    };

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
    };

    return (
        <div className="pageBody">
            <ToastContainer />
            <div className="commonDataTableHead">
                <div className="d-flex justify-content-between align-items-center w-100">
                    <div className="d-flex align-items-center" style={{ gap: "10px" }}>
                        <h6 className="commonTableTitle">Account Head</h6>
                    </div>
                    <div className="">
                        <input
                            type="text"
                            placeholder="Search..."
                            value={searchQuery}
                            className="searchInput"
                            onChange={handleSearchChange}
                        />
                    </div>
                    <div className="d-flex align-items-center" style={{ gap: 6 }}>
                        <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-top">Filter</Tooltip>}>
                            <Button className="secondaryBtn" variant="secondary" onClick={handleShowFilterModal}>
                                <MdFilterList />
                            </Button>
                        </OverlayTrigger>
                        <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-top">Add</Tooltip>}>
                            <Button className="primaryBtn" variant="primary" onClick={() => navigate("/finance/addaccounthead")}>
                                <MdAddCircle />
                            </Button>
                        </OverlayTrigger>
                    </div>
                </div>
            </div>

            <div className="commonTable">
                <div className="tableBody">
                    {isLoading ? (
                        <div className="loadingContainer">
                            <img src={loading} alt="Loading..." className="loadingGif" />
                        </div>
                    ) : (
                        <AppDataGrid
                            rows={filteredRecords.length ? filteredRecords : []}
                            columns={columns}
                            getRowId={(row) => row.account_head_id}
                            pageSizeOptions={[10, 20, 50]}
                            showToolbar={false}
                            pagination
                            density="compact"
                            maxHeight={550}
                        />
                    )}
                </div>
            </div>

            <Modal show={showFilterModal} onHide={handleCloseFilterModal} className="commonFilterModal">
                <Modal.Header closeButton className="modalHeaderFixed">
                    <Modal.Title>Filter</Modal.Title>
                </Modal.Header>
                <Modal.Body className="modalBodyScrollable">
                    <Row>
                        <Col xs={12}>
                            <div className='commonInput'>
                                <Form.Group controlId="account_head_name">
                                    <Form.Label>Account Head Name</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Enter Account Head Name"
                                        maxLength={100}
                                        value={filter.account_head_name}
                                        onChange={(e) => setFilter({ ...filter, account_head_name: e.target.value })}
                                    />
                                </Form.Group>
                            </div>
                        </Col>
                        <Col xs={12}>
                            <div className='commonInput'>
                                <Form.Group controlId="group_id">
                                    <Form.Label>Group Name</Form.Label>
                                    {/* ✅ FIX TC2: Only Active groups shown in dropdown */}
                                    <Form.Select
                                        value={filter.group_id}
                                        onChange={(e) => setFilter({ ...filter, group_id: e.target.value })}
                                    >
                                        <option value="">Select Group Name</option>
                                        {groups
                                            .filter((g) => g.is_active === "Active")
                                            .map((g) => (
                                                <option key={g.group_id} value={g.group_id}>
                                                    {g.group_name}
                                                </option>
                                            ))}
                                    </Form.Select>
                                </Form.Group>
                            </div>
                        </Col>
                        {/* ✅ FIX TC1: Account Head Code filter field added to modal */}
                        <Col xs={12}>
                            <div className='commonInput'>
                                <Form.Group controlId="account_head_code">
                                    <Form.Label>Account Head Code</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Enter Account Head Code"
                                        maxLength={50}
                                        value={filter.account_head_code}
                                        onChange={(e) => setFilter({ ...filter, account_head_code: e.target.value })}
                                    />
                                </Form.Group>
                            </div>
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer className="modalFooterFixed">
                    <Button variant="secondary" className="btn-info clearBtn me-2" onClick={handleReset}>
                        Reset
                    </Button>
                    <div>
                        <Button variant="secondary" className="btn-danger secondaryBtn me-2" onClick={handleCloseFilterModal}>
                            Cancel
                        </Button>
                        <Button variant="primary" className="btn-success primaryBtn" onClick={handleFilterSubmit}>
                            Search
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default AccountHead;